/**
 * Account Settings → Danger — self-service account deletion (#1748 Slice 1).
 *
 * Fixes the previously-broken button: it called `authClient.deleteUser()`
 * → `/api/auth/delete-user`, which was never implemented (404). It now
 * posts to `/api/account/delete` via `accountAPI`, scheduling a 30-day
 * soft-delete — orgs the user solely owns are held (recoverable) with any
 * paid plan canceled at period end, shared orgs are left, and the account
 * is signed out.
 *
 * Confirmation requires BOTH the typed email (deliberate intent) and the
 * password (genuine re-auth, verified server-side). If the user is the
 * sole owner of a shared org that still has other members, the server
 * refuses (409) and we surface the blocking orgs — nothing is deleted.
 *
 * The screen never displays the account's email address. It used to sit
 * in the confirm field's placeholder, which defeated the confirmation
 * twice over: it was the answer, available to copy, so typing it proved
 * nothing about intent; and it read as a filled-in field, so people
 * assumed the form was complete and reported the disabled button as a
 * bug. Requiring recall is the entire point of the step — so the address
 * appears nowhere here, including in the mismatch hint, which says only
 * that the input doesn't match.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
} from '@/components/ui'
import { accountAPI, type AccountDeleteResult } from '@/lib/api'
import { useIdentity } from '@/hooks/useIdentity'

export function AccountDanger() {
  const { state } = useIdentity()
  const navigate = useNavigate()
  const [showConfirm, setShowConfirm] = useState(false)
  const [typedEmail, setTypedEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [blockedOrgs, setBlockedOrgs] = useState<
    Array<{ id: string; name: string; memberCount: number }> | null
  >(null)

  if (state.status !== 'ready') return null
  const { user } = state.identity
  // #2510: 0 = no hold, the account and solely-owned orgs are deleted
  // permanently on the spot; a positive value keeps the reversible hold.
  const recoveryWindowDays = state.identity.recoveryWindowDays ?? 0
  const permanent = recoveryWindowDays === 0

  const emailMatches = typedEmail.trim().toLowerCase() === user.email.toLowerCase()
  const canConfirm = emailMatches && password.length > 0 && !busy
  // Only once they've actually typed something. An empty box isn't a
  // mistake yet, and flagging it as one on open is just noise.
  const showEmailMismatch = typedEmail.trim().length > 0 && !emailMatches

  function closeDialog() {
    setShowConfirm(false)
    setTypedEmail('')
    setPassword('')
    setDialogError(null)
  }

  async function handleDelete() {
    setDialogError(null)
    setBusy(true)
    let result: AccountDeleteResult
    try {
      result = await accountAPI.remove(password)
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Something went wrong. Try again.')
      setBusy(false)
      return
    }
    if (result.ok) {
      // Server revoked our session — go to login. Recovery is an explicit
      // action there, available for 30 days.
      navigate('/auth/login', { replace: true })
      return
    }
    setBusy(false)
    if (result.reason === 'reauth') {
      setDialogError(result.message)
      return
    }
    if (result.reason === 'block') {
      setBlockedOrgs(result.orgs)
      closeDialog()
      return
    }
    setDialogError(result.message)
  }

  return (
    <div className="space-y-4 max-w-xl">
      <div>
        <h3 className="text-base font-semibold">Delete account</h3>
        <p className="text-sm text-muted-foreground">
          {permanent ? (
            <>
              Delete your account and sign out everywhere. This permanently
              deletes you and any organizations you solely own right away — it
              can&apos;t be undone. You&apos;re removed from any shared
              organizations too.
            </>
          ) : (
            <>
              Delete your account and sign out everywhere. Organizations you own
              are held for {recoveryWindowDays} days — log back in during that
              time to restore them. You&apos;re removed from any shared
              organizations right away.
            </>
          )}
        </p>
      </div>

      {blockedOrgs && (
        <Card className="border-destructive/40">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-medium">You can't delete your account yet.</p>
            <p className="text-sm text-muted-foreground">
              You're the only owner of these organizations, and they still have
              other members. Transfer ownership or remove the members first:
            </p>
            <ul className="text-sm list-disc pl-5">
              {blockedOrgs.map((o) => (
                <li key={o.id}>
                  {o.name}{' '}
                  <span className="text-muted-foreground">({o.memberCount} members)</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card className="border-destructive/40">
        <CardContent className="p-4 space-y-3">
          <p className="text-sm">
            {permanent ? (
              <>
                Your sessions, API tokens, and the organizations you solely own
                are permanently deleted immediately — this can&apos;t be undone.
                Any paid plan is canceled at the end of the current billing
                period.
              </>
            ) : (
              <>
                Your sessions and API tokens are removed immediately. After{' '}
                {recoveryWindowDays} days your held organizations and their data
                are permanently deleted — this can&apos;t be undone. Any paid
                plan is canceled at the end of the current billing period.
              </>
            )}
          </p>
          <Button
            variant="destructive"
            onClick={() => {
              setBlockedOrgs(null)
              setShowConfirm(true)
            }}
          >
            Delete my account
          </Button>
        </CardContent>
      </Card>

      <Dialog open={showConfirm} onOpenChange={(o) => !o && !busy && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account</DialogTitle>
            <DialogDescription>
              {permanent ? (
                <>
                  Your account and the organizations you solely own will be
                  permanently deleted right away — this can&apos;t be undone.
                  Confirm your email and password to continue.
                </>
              ) : (
                <>
                  Your account will be scheduled for deletion and held for{' '}
                  {recoveryWindowDays} days — log back in during that time to
                  restore it. Confirm your email and password to continue.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="confirm-email">Type your email to confirm</Label>
                <Input
                  id="confirm-email"
                  value={typedEmail}
                  onChange={(e) => setTypedEmail(e.target.value)}
                  autoComplete="off"
                  aria-describedby={showEmailMismatch ? 'confirm-email-hint' : undefined}
                />
                {showEmailMismatch && (
                  // Says only that it doesn't match — never what would.
                  <p id="confirm-email-hint" className="text-xs text-muted-foreground">
                    That doesn't match the email address on this account.
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirm-password">Confirm your password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              {dialogError && (
                <p className="text-sm text-destructive" role="alert">
                  {dialogError}
                </p>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!canConfirm}>
              {busy ? 'Deleting…' : 'Delete account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
